import { HeartPulse, PlugZap } from "lucide-react";
import { cn } from "@/lib/utils";
import whatsapp from "@/assets/integrations/whatsapp.jpg";
import strava from "@/assets/integrations/strava.jpg";
import googleCalendar from "@/assets/integrations/google-calendar.svg";
import gmail from "@/assets/integrations/gmail.svg";
import googleDrive from "@/assets/integrations/google-drive.svg";
import googleTasks from "@/assets/integrations/google-tasks.svg";
import outlook from "@/assets/integrations/outlook.svg";
import onedrive from "@/assets/integrations/onedrive.svg";
import microsoftTeams from "@/assets/integrations/microsoft-teams.svg";
import googleContacts from "@/assets/integrations/google-contacts.png";
import microsoftTodo from "@/assets/integrations/microsoft-todo.jpg";
import appleContacts from "@/assets/integrations/apple-contacts.jpg";
import appleReminders from "@/assets/integrations/apple-reminders.jpg";
import appleCalendar from "@/assets/integrations/apple-calendar.jpg";
import appleHealth from "@/assets/integrations/apple-health.jpg";
import healthConnect from "@/assets/integrations/health-connect.png";
import dropbox from "@/assets/integrations/dropbox.jpg";
import todoist from "@/assets/integrations/todoist.jpg";
import trello from "@/assets/integrations/trello.jpg";
import slack from "@/assets/integrations/slack.jpg";
import notion from "@/assets/integrations/notion.jpg";
import spotify from "@/assets/integrations/spotify.jpg";

type BrandArtwork = { src: string; appIcon?: boolean };
// Unmodified publisher artwork, bundled locally. Provenance: assets/integrations/sources.json.
const BRAND: Readonly<Record<string, BrandArtwork | undefined>> = {
  whatsapp: { src: whatsapp, appIcon: true },
  strava: { src: strava, appIcon: true },
  google_calendar: { src: googleCalendar },
  gmail: { src: gmail },
  google_drive: { src: googleDrive },
  google_tasks: { src: googleTasks },
  outlook: { src: outlook },
  outlook_mail: { src: outlook },
  onedrive: { src: onedrive },
  microsoft_teams: { src: microsoftTeams },
  google_contacts: { src: googleContacts, appIcon: true },
  // Microsoft Graph contacts belong to Outlook, as do its mail and calendar.
  microsoft_contacts: { src: outlook },
  microsoft_todo: { src: microsoftTodo, appIcon: true },
  apple_contacts: { src: appleContacts, appIcon: true },
  apple_reminders: { src: appleReminders, appIcon: true },
  apple_calendar: { src: appleCalendar, appIcon: true },
  apple_health: { src: appleHealth, appIcon: true },
  health_connect: { src: healthConnect, appIcon: true },
  dropbox: { src: dropbox, appIcon: true },
  todoist: { src: todoist, appIcon: true },
  trello: { src: trello, appIcon: true },
  slack: { src: slack, appIcon: true },
  notion: { src: notion, appIcon: true },
  spotify: { src: spotify, appIcon: true },
};

export function ConnectionBrandMark({ provider, className, size = "md" }: {
  provider: string; className?: string; size?: "sm" | "md" | "lg";
}) {
  const entry = BRAND[provider];
  const box = size === "sm" ? "h-10 w-10 rounded-xl" : size === "lg" ? "h-14 w-14 rounded-2xl" : "h-12 w-12 rounded-xl";
  const iconSize = size === "sm" ? 28 : size === "lg" ? 40 : 34;
  const tileSize = size === "sm" ? 40 : size === "lg" ? 56 : 48;
  return <span
    data-brand={provider}
    className={cn("inline-flex shrink-0 items-center justify-center overflow-hidden bg-muted/40 ring-1 ring-inset ring-black/5 dark:ring-white/10", box, className)}
    aria-hidden="true"
  >
    {entry ? <img
      src={entry.src}
      alt=""
      width={entry.appIcon ? tileSize : iconSize}
      height={entry.appIcon ? tileSize : iconSize}
      className={cn("shrink-0 object-contain", entry.appIcon && "h-full w-full")}
      decoding="async"
      draggable={false}
    /> : provider.includes("health") ? <HeartPulse size={iconSize} className="text-rose-500" /> : <PlugZap size={iconSize} className="text-primary" />}
  </span>;
}
export function ConnectionBrandInline({ provider, label }: { provider: string; label: string }) {
  return <span className="inline-flex min-w-0 items-center gap-2">
    <ConnectionBrandMark provider={provider} size="sm" />
    <span className="truncate text-xs font-medium text-foreground">{label}</span>
  </span>;
}
